/**
 * Handles submission for the contact page form with improved feedback logic.
 * Adjusted for the new vendor-provided HTML structure.
 */
export const initPageContactForm = (): void => {
  // Target the form directly by our unique app class
  const form = document.querySelector<HTMLFormElement>(".app-contact-form");

  if (!form) {
    return;
  }

  // The messages container is located inside the form in the new structure
  const messagesContainer = form.querySelector<HTMLDivElement>(
    ".form-messages-container"
  );

  if (!messagesContainer) {
    console.warn(
      "[Contact Form] Messages container not found inside the form."
    );
    return;
  }

  const successTpl =
    window.themeVariables?.formTranslations?.contactForm?.status
      ?.successMessage || "<strong>Success!</strong> Message sent.";
  const errorTpl =
    window.themeVariables?.formTranslations?.contactForm?.status
      ?.errorMessage || "<strong>Error!</strong> Message not sent.";

  const createAlert = (type: "success" | "error", message: string): string => {
    return `
      <div class="alert alert--${type}">
        <p class="alert__text">${message}</p>
      </div>
    `;
  };

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    // Prevent vendor scripts (like ajax-contact in main.js) from running
    e.stopImmediatePropagation();

    const submitButton = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    );
    if (!submitButton) return;

    // 1. Clear previous messages and disable the button
    messagesContainer.innerHTML = "";
    submitButton.disabled = true;
    submitButton.style.opacity = "0.7";

    // Safety check for the localized nonce
    if (!window.themeVariables?.pageContactFormNonce) {
      console.error("Page contact form nonce not found.");
      messagesContainer.innerHTML = createAlert(
        "error",
        "Configuration error. Please reload the page."
      );
      submitButton.disabled = false;
      submitButton.style.opacity = "1";
      return;
    }

    const formData = new FormData(form);
    formData.append("action", "submit_page_contact_form");
    formData.append(
      "contactFormNonce",
      window.themeVariables.pageContactFormNonce
    );

    try {
      const response = await fetch(window.themeVariables.ajaxUrl, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        // SUCCESS: Display success message and hide the form elements
        const message = result.data?.message || successTpl;
        messagesContainer.innerHTML = createAlert("success", message);

        // Hide the inputs but keep the message visible
        const rowElements = form.querySelectorAll(".row, .form-title");
        rowElements.forEach(
          (el) => ((el as HTMLElement).style.display = "none")
        );
      } else {
        // ERROR: Display error message
        const message = result.data?.message || errorTpl;
        messagesContainer.innerHTML = createAlert("error", message);
        submitButton.disabled = false;
        submitButton.style.opacity = "1";
      }
    } catch (error) {
      console.error("Form submission error:", error);
      messagesContainer.innerHTML = createAlert("error", errorTpl);
      submitButton.disabled = false;
      submitButton.style.opacity = "1";
    }
  });

  console.log("[Forms] Page Contact Form initialized.");
};
